import React, { ChangeEvent, DragEvent, useEffect, useRef, useState, useContext } from 'react'
import { Context } from '@/shared/store/reducers/global-reducer'

import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'

import { useAuth0 } from '@/auth/index'
import useMounted from '@/components/hooks/use-mounted'
import { uploadProjectFile } from '@/shared/services/prodigy-file'
import type { ProjectFileUploadMetadata } from '@/shared/services/prodigy-file'

const ACCEPTED_FILE_TYPES = [
  'audio/*',
  'video/*',
  'image/*',
  'text/*',
  'application/*',
  'image/jpeg',
  '.pdf',
].join(',')

type UploadStatus = 'uploading' | 'success' | 'error'

type UploadTask = {
  id: string
  filename: string
  progress: number
  status: UploadStatus
  error: string
  uploadedFile: ProjectFileUploadMetadata | null
}

const useStyles = makeStyles((theme) => ({
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    background: 'rgba(38, 43, 51, 0.48)',
  },
  root: {
    width: 420,
    maxWidth: '100%',
    padding: theme.spacing(2),
    borderRadius: 16,
    background: '#f5f8fc',
    border: '1px solid rgba(34, 80, 153, 0.2)',
    boxShadow: '0 24px 56px rgba(20, 35, 64, 0.32)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  title: {
    color: '#225099',
    fontWeight: 700,
  },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  closeButton: {
    padding: 4,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  dropZone: {
    display: 'flex',
    minHeight: 132,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2.5),
    borderRadius: 14,
    border: '2px dashed rgba(34, 80, 153, 0.32)',
    background: '#ffffff',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 160ms ease, background-color 160ms ease, transform 160ms ease',
    '&:hover': {
      borderColor: '#225099',
      background: '#eef4fb',
    },
  },
  dropZoneActive: {
    borderColor: '#225099',
    background: '#e7f0fb',
    transform: 'translateY(-1px)',
  },
  taskList: {
    display: 'flex',
    maxHeight: 220,
    flexDirection: 'column',
    gap: theme.spacing(1),
    overflowY: 'auto',
  },
  taskItem: {
    padding: theme.spacing(1),
    borderRadius: 10,
    background: '#ffffff',
    border: '1px solid rgba(34, 80, 153, 0.14)',
  },
  taskHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  taskStatus: {
    flexShrink: 0,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  taskUploading: {
    color: '#225099',
  },
  taskError: {
    color: theme.palette.error.main,
  },
  iconBubble: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: '#225099',
    color: '#ffffff',
    fontSize: '1.5rem',
    fontWeight: 700,
  },
  filename: {
    width: '100%',
    color: theme.palette.text.primary,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  progressWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  statusSuccess: {
    color: '#17663a',
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  input: {
    display: 'none',
  },
  error: {
    color: theme.palette.error.main,
  },
}))

export const ProjectItemFileEditComponent = (props: any) => {
  const { handleCancel, handleChange, project } = props
  const classes = useStyles()
  const { getIdTokenClaims, user } = useAuth0()
  const { store } = useContext(Context)
  const { activeTenant } = store
  const mountedRef = useMounted(true)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const uploadControllersRef = useRef<Map<string, AbortController>>(new Map())
  const uploadSequenceRef = useRef(0)
  const [tasks, setTasks] = useState<UploadTask[]>([])
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    return () => {
      mountedRef.current = false
      uploadControllersRef.current.forEach((controller) => controller.abort())
      uploadControllersRef.current.clear()
    }
  }, [mountedRef])

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const updateTask = (id: string, patch: Partial<UploadTask>) => {
    setTasks((currentTasks) =>
      currentTasks.map((task) => (task.id === id ? { ...task, ...patch } : task)),
    )
  }

  const uploadFile = async (selectedFile: File) => {
    const id = `${Date.now()}-${uploadSequenceRef.current}`
    uploadSequenceRef.current += 1

    const controller = new AbortController()
    uploadControllersRef.current.set(id, controller)
    setTasks((currentTasks) => [
      ...currentTasks,
      {
        id,
        filename: selectedFile.name,
        progress: 0,
        status: 'uploading',
        error: '',
        uploadedFile: null,
      },
    ])

    try {
      const nextUploadedFile = await uploadProjectFile(selectedFile, {
        getToken: async () => {
          const claims = await getIdTokenClaims()
          if (!claims?.__raw) {
            throw new Error('Unable to acquire Auth0 ID token for file upload')
          }
          return claims.__raw
        },
        signal: controller.signal,
        onProgress: (percentage) => {
          if (!mountedRef.current) return
          updateTask(id, { progress: percentage })
        },
        tenantId: activeTenant?.id,
        projectId: project?.id,
        uploadedBy: user?.email || undefined,
        projectNumber: project?.projectNumber,
        brandId: project?.brandId || project?.brand?.id,
        agencyId: project?.agencyId || project?.agency?.id,
      })

      if (!mountedRef.current) return
      updateTask(id, {
        progress: 100,
        status: 'success',
        uploadedFile: nextUploadedFile,
      })
    } catch (uploadError) {
      if (!mountedRef.current) return
      resetInput()
      updateTask(id, {
        progress: 0,
        status: 'error',
        error: uploadError instanceof Error ? uploadError.message : 'Unable to upload file',
      })
    } finally {
      uploadControllersRef.current.delete(id)
    }
  }

  const uploadFiles = async (selectedFiles: FileList | File[]) => {
    const files = Array.from(selectedFiles)
    resetInput()
    files.forEach((selectedFile) => {
      uploadFile(selectedFile)
    })
  }

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files

    if (!selectedFiles?.length) {
      return
    }

    await uploadFiles(selectedFiles)
  }

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)

    const droppedFiles = event.dataTransfer.files
    if (droppedFiles?.length) {
      await uploadFiles(droppedFiles)
    }
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  const handleClose = () => {
    const uploadedFiles = tasks
      .map((task) => task.uploadedFile)
      .filter(Boolean) as ProjectFileUploadMetadata[]

    if (uploadedFiles.length) {
      handleChange(uploadedFiles)
      return
    }

    handleCancel()
  }

  const handleCancelClick = () => {
    uploadControllersRef.current.forEach((controller) => controller.abort())
    uploadControllersRef.current.clear()
    handleCancel()
  }

  const hasActiveUploads = tasks.some((task) => task.status === 'uploading')
  const successfulTasks = tasks.filter((task) => task.status === 'success')
  const failedTasks = tasks.filter((task) => task.status === 'error')
  const emptyStateText = tasks.length ? 'Drop more files here' : 'Drop files here'
  const activeUploadsCount = tasks.filter((task) => task.status === 'uploading').length

  const getStatusText = () => {
    if (hasActiveUploads) return `${activeUploadsCount} uploading`
    if (successfulTasks.length && failedTasks.length) {
      return `${successfulTasks.length} uploaded, ${failedTasks.length} failed`
    }
    if (successfulTasks.length) return `${successfulTasks.length} uploaded`
    if (failedTasks.length) return `${failedTasks.length} failed`
    return ''
  }

  const statusText = getStatusText()
  const actionLabel = hasActiveUploads || !tasks.length ? 'Cancel' : 'Close'

  const handleDismiss = () => {
    if (hasActiveUploads || !tasks.length) {
      handleCancelClick()
      return
    }

    handleClose()
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleDismiss()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  })

  return (
    <div className={classes.backdrop}>
      <Paper className={classes.root} elevation={0}>
        <input
          ref={inputRef}
          className={classes.input}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          multiple
          onChange={handleFileSelected}
        />

        <div className={classes.header}>
          <Typography className={classes.title}>Upload files</Typography>
          <div className={classes.headerControls}>
            {hasActiveUploads ? <CircularProgress size={18} /> : null}
            <IconButton
              aria-label="close file upload"
              className={classes.closeButton}
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={classes.body}>
          <div
            className={[classes.dropZone, isDragging ? classes.dropZoneActive : '']
              .filter(Boolean)
              .join(' ')}
            role="button"
            tabIndex={0}
            onClick={() => {
              inputRef.current?.click()
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                inputRef.current?.click()
              }
            }}
          >
            <div className={classes.iconBubble}>+</div>
            <Typography variant="body1">{emptyStateText}</Typography>
            <Typography variant="body2">
              Drag and drop, or click to choose from your computer.
            </Typography>
          </div>

          {statusText ? (
            <Typography variant="body2" className={classes.statusSuccess}>
              {statusText}
            </Typography>
          ) : null}

          {tasks.length ? (
            <div className={classes.taskList}>
              {tasks.map((task) => (
                <div key={task.id} className={classes.taskItem}>
                  <div className={classes.taskHeader}>
                    <Typography variant="body2" className={classes.filename}>
                      {task.filename}
                    </Typography>
                    <Typography
                      variant="caption"
                      className={[
                        classes.taskStatus,
                        task.status === 'uploading' ? classes.taskUploading : '',
                        task.status === 'success' ? classes.statusSuccess : '',
                        task.status === 'error' ? classes.taskError : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {task.status === 'success' ? 'OK' : task.status}
                    </Typography>
                  </div>

                  {task.status === 'uploading' ? (
                    <div className={classes.progressWrap}>
                      <LinearProgress variant="determinate" value={task.progress} />
                      <Typography variant="caption">
                        {Math.round(task.progress)}% uploaded
                      </Typography>
                    </div>
                  ) : null}

                  {task.status === 'error' ? (
                    <Typography variant="caption" className={classes.error}>
                      {task.error}
                    </Typography>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          <div className={classes.actions}>
            <Button size="small" color="primary" onClick={handleDismiss}>
              {actionLabel}
            </Button>
          </div>
        </div>
      </Paper>
    </div>
  )
}

export default ProjectItemFileEditComponent
